import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, Menu, X, ChevronRight, Search } from 'lucide-react';
import NotificationBell from './NotificationBell';
import ProfileMenu from './ProfileMenu';
import ViewModeToggle from './ViewModeToggle';
import SearchSuggestions from './SearchSuggestions';
import { navItems, childMatches, getPageTitle } from './navConfig';
import { featureOff, useFeatureFlags } from '../../utils/featureFlags';
import InboxUnreadBadge from './InboxUnreadBadge';
import CommandPalette from './CommandPalette';
import { flattenLeaves } from '../../utils/appMenu';
import { customerAPI, gtplSaathiAPI } from '../../services/api';
import toast from 'react-hot-toast';
import useAppearance from '../../theme/useAppearance';
import { HIDE_GTPL } from '../../utils/demo';
import { brandFor } from '../../utils/appBrand';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';
import SubscriptionBanner from './SubscriptionBanner';

const FOLDED_BY_DEFAULT = ['NETWORK', 'GTPL', 'ADMIN'];

export default function AdminLayout({ viewMode, onToggleViewMode }) {
  const brand = brandFor(useCompanyIdentity('catv'));
  const { user, logout, isAdmin, hasModuleRight } = useAuth();
  useAppearance();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  // GTPL section's own on/off preference (Settings → GTPL Saathi) — only fetched for
  // users who could possibly see the section at all.
  const flags = useFeatureFlags(); // pages of features switched off for this operator are left out of the menu
  const canSeeGtpl = isAdmin || hasModuleRight('gtplSaathi');
  const [gtplShowInNav, setGtplShowInNav] = useState(!HIDE_GTPL); // demo and operator hostnames never show the GTPL section
  useEffect(() => {
    if (!canSeeGtpl) return;
    gtplSaathiAPI.getSessionStatus()
      .then(({ data }) => setGtplShowInNav(!HIDE_GTPL && data.data?.showInNav !== false))
      .catch(() => {});
  }, [canSeeGtpl]);
  const findActiveDropdown = (pathname, search) => {
    const match = navItems.find(n => n.children && n.children.some(c => childMatches(c, pathname, search)));
    return match ? match.key : null;
  };
  const [openDropdown, setOpenDropdown] = useState(() => findActiveDropdown(location.pathname, location.search));

  useEffect(() => {
    const active = findActiveDropdown(location.pathname, location.search);
    if (active) setOpenDropdown(active);
  }, [location.pathname, location.search]);

  // Same visibility rules as the sidebar above, flattened for the Ctrl+K palette.
  const paletteLeaves = flattenLeaves({
    navItems,
    canSee: (item) => !(item.key === 'gtpl' && !gtplShowInNav) && !featureOff(item.to || item.groupTo, flags)
      && (!item.adminOnly || isAdmin || (item.moduleKey && hasModuleRight(item.moduleKey))),
    visibleChildren: (item) => item.children.filter(c =>
      (!c.moduleKey || isAdmin || hasModuleRight(c.moduleKey)) && (!c.adminOnly || isAdmin) && !featureOff(c.to, flags)),
  });

  const isImpersonating = !!localStorage.getItem('adminToken');
  const impersonatingName = localStorage.getItem('impersonatingName');

  // Sidebar sections the user has folded (remembered per browser). Rarely used ones start folded;
  // the section holding the current page always stays open so the active item is never hidden.
  const [folded, setFolded] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav.folded')) || FOLDED_BY_DEFAULT; } catch { return FOLDED_BY_DEFAULT; }
  });
  let activeSection = '';
  let currentSection = '';
  navItems.forEach(n => {
    if (n.section) { currentSection = n.section; return; }
    const hit = n.children ? n.children.some(c => childMatches(c, location.pathname, location.search))
      : (location.pathname === n.to || location.pathname.startsWith(n.to + '/'));
    if (hit) activeSection = currentSection;
  });
  currentSection = '';
  const isFolded = (s) => folded.includes(s) && s !== activeSection;
  const toggleSection = (s) => setFolded(f => {
    const next = f.includes(s) ? f.filter(x => x !== s) : [...f, s];
    localStorage.setItem('nav.folded', JSON.stringify(next));
    return next;
  });

  const handleSwitchBack = () => {
    const adminToken = localStorage.getItem('adminToken');
    const adminRefresh = localStorage.getItem('adminRefreshToken');
    localStorage.setItem('token', adminToken);
    if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('impersonatingName');
    window.location.href = '/agent-collection';
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    try {
      const results = await customerAPI.search(q, 1);
      const match = results[0];
      if (!match) { toast.error('No customer found'); return; }
      navigate(`/customers/${match._id}`);
      setSearchQuery('');
      setSuggestOpen(false);
      setSearchOpen(false);
    } catch {
      toast.error('Search failed');
    }
  };

  const handlePickSuggestion = (customer) => {
    navigate(`/customers/${customer._id}`);
    setSearchQuery('');
    setSuggestOpen(false);
    setSearchOpen(false);
  };

  const handleViewAllResults = (q) => {
    navigate(`/customers?search=${encodeURIComponent(q)}`);
    setSearchQuery('');
    setSuggestOpen(false);
    setSearchOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 90 }}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <img src={brand.logo} alt={brand.name} style={{ height: 72, objectFit: 'contain', maxWidth: '100%' }} />
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item, i) => {
            if (item.section) {
              // The GTPL heading goes with its items (hidden on the demo, operator copies, or by the Settings switch).
              if (item.section === 'GTPL' && !gtplShowInNav) return null;
              currentSection = item.section;
              const folded = isFolded(item.section);
              return (
                <button key={i} type="button" className="nav-section-label" onClick={() => toggleSection(item.section)}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                  {item.section}
                  <ChevronRight size={12} style={{ transition: 'transform .15s', transform: folded ? 'none' : 'rotate(90deg)' }} />
                </button>
              );
            }
            // Items of a folded section stay out of the list until its heading is opened
            // (or the current page lives in it — see activeSection).
            if (isFolded(currentSection)) return null;
            if (item.adminOnly && !isAdmin && !(item.moduleKey && hasModuleRight(item.moduleKey))) return null;
            if (item.key === 'gtpl' && !gtplShowInNav) return null;
            if (featureOff(item.to || item.groupTo, flags)) return null;

            if (item.children) {
              const isOpen = openDropdown === item.key;
              const isParentActive = item.children.some(c => childMatches(c, location.pathname, location.search));
              // Children may carry their own gate (Complaints/Requests, GTPL's admin-only
              // Session & Settings) — hidden if the user lacks the moduleKey or isn't a
              // full admin, and a group left with no visible child disappears entirely
              // rather than opening onto an empty dropdown.
              const children = item.children.filter(c =>
                (!c.moduleKey || isAdmin || hasModuleRight(c.moduleKey)) && (!c.adminOnly || isAdmin) && !featureOff(c.to, flags));
              if (!children.length) return null;
              return (
                <div key={item.key}>
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(o => o === item.key ? null : item.key)}
                    className={`nav-item ${isParentActive ? 'active' : ''}`}
                    style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                  >
                    <item.icon size={16} />
                    {item.label}
                    <ChevronRight size={14} style={{ marginLeft: 'auto', transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                  </button>
                  {isOpen && children.map(child => {
                    const childActive = childMatches(child, location.pathname, location.search);
                    return (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={`nav-item ${childActive ? 'active' : ''}`}
                        style={{ paddingLeft: 46, fontSize: '.82rem' }}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <child.icon size={14} />
                        {child.label}
                      </NavLink>
                    );
                  })}
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={16} />
                {item.label}
                {item.badge === 'whatsappUnread' && (isAdmin || hasModuleRight('whatsappInbox')) && <InboxUnreadBadge />}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar-circle">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="sidebar-user-info">
              <p>{user?.name}</p>
              <span>{user?.role}</span>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={13} style={{ display: 'inline', marginRight: 6 }} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <header className="topbar">
          <div className="flex items-center gap-3" style={{ flexShrink: 0 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="mobile-menu-btn"
            >
              <Menu size={20} />
            </button>
            <span className="topbar-title">{getPageTitle(location.pathname)}</span>
          </div>

          <form onSubmit={handleSearch} className="topbar-search">
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              className="search-input"
              style={{ width: '100%', paddingLeft: 34 }}
              placeholder="Search customer (mobile, name, account, STB)..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setSuggestOpen(false)}
            />
            {suggestOpen && <SearchSuggestions query={searchQuery} onPick={handlePickSuggestion} onViewAll={handleViewAllResults} />}
          </form>

          <div className="topbar-right">
            {/* Mobile search toggle */}
            <button className="mobile-search-btn" onClick={() => { setSearchOpen(o => !o); setSearchQuery(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, borderRadius: 6 }}>
              {searchOpen ? <X size={18} /> : <Search size={18} />}
            </button>
            <ViewModeToggle viewMode={viewMode} onToggle={onToggleViewMode} />
            <NotificationBell />
            <ProfileMenu user={user} onLogout={handleLogout} size={30} />
          </div>
        </header>

        {/* Impersonation banner */}
        {isImpersonating && (
          <div style={{ background: 'var(--cb-f59e0b)', color: 'var(--c-1c1917)', padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: '.83rem', fontWeight: 600 }}>
            <span>👤 Viewing as agent: <strong>{impersonatingName}</strong></span>
            <button onClick={handleSwitchBack} style={{ background: 'var(--cb-1c1917)', color: 'var(--c-ffffff)', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
              ← Switch Back to Admin
            </button>
          </div>
        )}

        {/* Mobile expanded search bar */}
        {searchOpen && (
          <form onSubmit={handleSearch} className="mobile-search-bar">
            <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              className="search-input"
              style={{ width: '100%', paddingLeft: 38, height: 40, borderRadius: 8 }}
              placeholder="Search customer (name, mobile, account, STB)..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setSuggestOpen(false)}
              autoFocus
            />
            {suggestOpen && <SearchSuggestions query={searchQuery} onPick={handlePickSuggestion} onViewAll={handleViewAllResults} />}
          </form>
        )}

        <main className="page-content">
          {isAdmin && <SubscriptionBanner />}
          <Outlet />
          <CommandPalette leaves={paletteLeaves} />
        </main>
      </div>
    </div>
  );
}
